import React, {FC, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useParams} from 'react-router-dom'
import {FormModelType, useForm} from 'reactjs-use-form'

import {Box, TextField} from '@mui/material'
import Typography from '@mui/material/Typography'
import {Theme, useTheme} from '@mui/material/styles'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import signupPngSrc from '@progyny/assets/images/side-banner-white-top.png'
import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import useChangeLanguage from '@progyny/components/Navigation/useChangeLanguage'
import PageLoader from '@progyny/components/PageLoader'
import {numericRegex, PasswordField} from '@progyny/components/PasswordField'
import {SidepanelContainer} from '@progyny/components/SidepanelContainer'
import {
  MeDocument,
  useVerifyRegistrationTokenMutation,
  useRegisterMutation,
} from '@progyny/generated/graphql'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'

import i18n from '@root/i18n'

const useStyles = makeStyles()((theme: Theme) => ({
  formContainer: {
    width: 420,
    '& h4': {
      fontWeight: 'normal',
    },
  },
  button: {
    margin: `${theme.spacing(4)} 0`,
    display: 'block',
  },
}))

const getEmailLanguage = () => {
  const url = new URL(window.location.href)
  const params = new URLSearchParams(url.search)
  return params.get('lang')
}

const CompleteRegistration: FC = () => {
  const {token} = useParams() as {token: string}
  const {getPath} = useRoutes()
  const history = useHistory()
  const {changeLanguage} = useChangeLanguage()

  const [verifyRegistrationToken] = useVerifyRegistrationTokenMutation()

  const [showForm, setShowForm] = React.useState(false)

  useEffect(() => {
    const emailLanguage = getEmailLanguage() || 'en'

    const setUiLanguage = async () => {
      await changeLanguage(emailLanguage)
    }
    if (i18n.language !== emailLanguage) setUiLanguage()
  }, [changeLanguage])

  useEffect(() => {
    setShowForm(false)

    verifyRegistrationToken({variables: {token: token}})
      .then(async (response) => {
        const data = response.data!.verifyRegistrationToken

        if (data!.success) {
          setShowForm(true)
        } else {
          // redirect to the 404 page
          history.push(getPath(RouteEnum.NOT_FOUND))
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [getPath, history, token, verifyRegistrationToken])

  if (showForm) {
    return <CompleteRegistrationForm />
  } else {
    return <PageLoader position='top' />
  }
}

const CompleteRegistrationForm: FC = () => {
  const {classes} = useStyles()
  const {t} = useTranslation()

  const {token} = useParams() as {token: string}
  const {getPath} = useRoutes()
  const user = useMeStore((s) => s.user)
  const history = useHistory()
  const {enqueueSnackbar} = useSnackbar()

  const [register, {loading}] = useRegisterMutation()

  const theme = useTheme()

  const formModel: FormModelType = {
    firstName: {
      value: '',
      required: true,
    },
    lastName: {
      value: '',
      required: true,
    },
    password: {
      value: '',
      required: true,
      validator: (password) => {
        if (password.length < 12) {
          return t('profile_security_password_too_short')
        } else if (numericRegex.test(password)) {
          return t('profile_security_password_numeric_only')
        } else return ''
      },
    },
  }

  useEffect(() => {
    if (!!user?.employeeNumber) {
      history.push(`${getPath(RouteEnum.LOGIN)}`)
    }
  }, [getPath, history, user])

  const handleSubmit = () => {
    register({
      variables: {
        token: token,
        firstName: values.firstName,
        lastName: values.lastName,
        password: values.password,
        language: localStorage.getItem('ui_language') || 'en',
      },
      refetchQueries: [{query: MeDocument}],
      awaitRefetchQueries: true,
    })
      .then((response) => {
        const data = response.data!.register

        if (data!.success) {
          // redirect to the "create account" page
          return history.push(getPath(RouteEnum.CREATE_ACCOUNT))
        } else if (data!.errors && data!.errors.length > 0) {
        } else {
          // show the first error message
          const error = data!.errors![0]
          const message =
            error!.field === '__all__'
              ? error!.messages[0]
              : `${error!.field}: ${error!.messages[0]}`
          enqueueSnackbar({message, variant: 'error'})
        }
      })
      .catch((error) => {
        console.error(error)
        enqueueSnackbar({
          message: error?.message || 'An unexpected error occurred.',
          variant: 'error',
        })
      })
  }

  const {values, errors, isDisabled, handleOnSubmit, handleOnChange} = useForm(
    formModel,
    handleSubmit
  )

  return (
    <SidepanelContainer
      styleProps={{
        backgroundPicSrc: signupPngSrc,
        backgroundColor: theme.palette.primary.main,
        logoColor: 'white',
      }}
      mainComponent={
        <Box className={classes.formContainer}>
          <Typography
            variant='h2'
            fontWeight={500}
            mb={theme.spacing(4)}
            textAlign={'center'}
          >
            {t('register_complete_register_to_progyny')}
          </Typography>
          <form onSubmit={handleOnSubmit}>
            <Box mt={3}>
              <Box mt={1}>
                <TextField
                  size='small'
                  fullWidth
                  label={t('register_complete_first_name')}
                  placeholder={t('register_complete_your_first_name')}
                  name='firstName'
                  value={values.firstName}
                  onChange={handleOnChange}
                  error={errors?.firstName.hasError}
                  helperText={t(errors.firstName.message)}
                />
              </Box>
            </Box>
            <Box mt={3}>
              <Typography variant='h6' id='last-name-label'></Typography>
              <Box mt={1}>
                <TextField
                  size='small'
                  fullWidth
                  label={t('register_complete_last_name')}
                  placeholder={t('register_complete_your_last_name')}
                  name='lastName'
                  value={values.lastName}
                  onChange={handleOnChange}
                  error={errors?.lastName.hasError}
                  helperText={t(errors.lastName.message)}
                />
              </Box>
            </Box>
            <Box mt={3}>
              <Box mt={1}>
                <PasswordField
                  label={t('register_complete_create_password')}
                  showStrength
                  onChange={handleOnChange}
                  errors={errors}
                  values={{password: values.password}}
                  placeholder={t('register_complete_choose_strong_password')}
                />
              </Box>
            </Box>
            <SubmitButton
              type='submit'
              loading={loading}
              disabled={isDisabled}
              className={classes.button}
              fullWidth
            >
              {t('routes_nav_register')}
            </SubmitButton>
          </form>
        </Box>
      }
    />
  )
}

export default CompleteRegistration
